import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import { AnchorHTMLAttributes } from 'react';

import { cn } from '@v2/shared/lib/utils';
import { CartCount } from '@v2/shared/components/CartCount';

interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** The url of the cart page. */
  cartUrl: string;
  /**
   * The number of items in the cart, displayed in the red badge by the cart icon — or null
   * while the cart has not loaded, which leaves the badge out entirely. Drawing a 0 until
   * the real count arrives would flash "0" and then "2" at a visitor who has two things in
   * their cart. The badge is absolutely positioned, so leaving it out moves nothing.
   */
  cartCount: number | null;
  /** When true, the Cart Link styles will be adjusted to fit the mobile navigation.
   * @default false
   */
  isMobile?: boolean;
}

export const CartLink = ({ cartUrl, cartCount, isMobile = false, ...props }: Props) => {
  /*
   * The count belongs in the accessible name. The badge is this link's only visible text,
   * and a name that leaves it out — "Go to Cart" — is a name a voice-control user cannot
   * speak: they read "3" and ask for it, and nothing matches. WCAG 2.5.3, Label in Name.
   *
   * The badge is not then announced twice. An aria-label replaces the subtree for naming,
   * so this string is the whole name a screen reader reads. Until the count is known
   * there is no badge to read, and the name claims no number.
   */
  const label = cartCount === null ? 'Cart' : `Cart, ${cartCount} ${cartCount === 1 ? 'item' : 'items'}`;

  return (
    <a
      href={cartUrl}
      className={cn(
        'text-2xl',
        'text-slate-800 hover:text-slate-900 active:text-slate-950',
        'group cursor-pointer relative',
        {
          'inline-flex p-2': isMobile,
          flex: !isMobile,
        }
      )}
      aria-label={label}
      {...props}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-md',
          'bg-white group-hover:bg-slate-100 group-active:bg-slate-200',
          {
            'w-9 h-9': isMobile,
            'h-12 px-3': !isMobile,
          }
        )}
      >
        <FontAwesomeIcon icon={faShoppingCart} />
      </div>
      {cartCount !== null && <CartCount className="absolute top-1 right-1" count={cartCount} />}
    </a>
  );
};
